import { Stack, Typography } from '@mui/material';
import { useCallback } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { XCloseIcon } from '@/components/icons/XCloseIcon';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { MarkSuggestionAsDuplicateAction } from '@/modules/aiSuggestionReviewCard/components/MarkSuggestionAsDuplicateAction';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { LocateSuggestionButton } from '@/modules/aiSuggestions/components/LocateSuggestionButton';
import { COLORS } from '@/styles/tokens/colors';

interface AIOnboardingSuggestionActionsProps {
  suggestion: AiSuggestionForReviewCard;
  disabled: boolean;
  locationContext?: string;
  onEvidenceClick: () => void;
  onAccept: () => void;
  onReject: () => void;
  onMarkedDuplicate: (suggestionID: string) => void;
  beginOperation: () => boolean;
  endOperation: () => void;
}

export function AIOnboardingSuggestionActions({
  suggestion,
  disabled,
  locationContext,
  onEvidenceClick,
  onAccept,
  onReject,
  onMarkedDuplicate,
  beginOperation,
  endOperation,
}: AIOnboardingSuggestionActionsProps) {
  const trackUserEvent = useTrackUserEvent();

  const handleAccept = useCallback(() => {
    trackUserEvent('ai_suggestion accept', {
      suggestionKind: suggestion.kind,
      count: 1,
    });
    onAccept();
  }, [onAccept, suggestion.kind, trackUserEvent]);

  const handleReject = useCallback(() => {
    trackUserEvent('ai_suggestion reject', {
      suggestionKind: suggestion.kind,
      count: 1,
    });
    onReject();
  }, [onReject, suggestion.kind, trackUserEvent]);

  return (
    <Stack
      direction="row"
      data-testid="ai-onboarding-suggestion-actions"
      gap={0.25}
      justifyContent="space-between"
      width="100%"
      alignItems="center"
    >
      <Stack direction="row" gap={0.5} alignItems="center">
        <LocateSuggestionButton
          suggestionID={suggestion.id}
          onLocateSuggestionClick={onEvidenceClick}
          disabled={disabled}
        />
        {locationContext && (
          <Typography variant="body-xs">{locationContext}</Typography>
        )}
      </Stack>
      <Stack direction="row" gap={0.25} alignItems="center">
        <MarkSuggestionAsDuplicateAction
          suggestion={suggestion}
          disabled={disabled}
          iconProps={{ size: 12 }}
          label="Mark duplicate"
          onMarkedDuplicate={onMarkedDuplicate}
          beginOperation={beginOperation}
          endOperation={endOperation}
          tooltipPlacement="top"
        />
        <Tooltip title="Accept suggestion" placement="top">
          <IconButton
            icon={CheckIcon}
            iconProps={{ size: 12, fill: 'transparent' }}
            ariaLabel="Accept suggestion"
            size="xs"
            variant="transparent"
            onClick={handleAccept}
            disabled={disabled}
            sx={{
              backgroundColor: COLORS.FUNCTIONAL.SUCCESS[100],
              color: COLORS.FUNCTIONAL.SUCCESS[700],
              '&:hover': {
                backgroundColor: COLORS.FUNCTIONAL.SUCCESS[200],
              },
            }}
          />
        </Tooltip>
        <Tooltip title="Reject suggestion" placement="top">
          <IconButton
            icon={XCloseIcon}
            iconProps={{ size: 12, fill: 'transparent' }}
            ariaLabel="Reject suggestion"
            size="xs"
            variant="transparent"
            onClick={handleReject}
            disabled={disabled}
            sx={{
              backgroundColor: COLORS.FUNCTIONAL.ERROR[100],
              color: COLORS.FUNCTIONAL.ERROR[700],
              '&:hover': {
                backgroundColor: COLORS.FUNCTIONAL.ERROR[200],
              },
            }}
          />
        </Tooltip>
      </Stack>
    </Stack>
  );
}
